import { message } from "antd";
import ConfirmPopup from "components/ConfirmPopup";
import DeletePopup from "components/DeletePopup";
import Vocabulary, { getVocabulary } from "components/Vocabulary";
import { useActiveUserContext } from "contexts/ActiveUserContext";
import useAPIResponse from "hooks/useAPIResponse";
import { EntityNavigatorParams } from "hooks/useEntityNavigator";
import useModalRenderer from "hooks/useModalRenderer";
import _, {
  difference,
  filter,
  find,
  forEach,
  get,
  isEmpty,
  map,
  size,
} from "lodash";
import moment from "moment";
import React, {
  Dispatch,
  ReactChild,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
//@ts-expect-error
import useTriggerableEffect from "react-triggerable-effect";
import { EmptyCancellableRequest } from "restAPI";
import ExceptionAPI from "restAPI/ExceptionAPI";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import ProductAPI from "restAPI/ProductAPI";
import SubProductAPI from "restAPI/SubProductAPI";
import TeamsAPI from "restAPI/TeamsAPI";
import UserAPI from "restAPI/UserAPI";
import { NameIdPair } from "types/api/nameIdPair";
import { UserInfoProjectorImpl } from "types/api/userInfoProjectorImpl";
import { ArmorcodeCustomField } from "types/tenantConfig";
import ModalUtil from "utils/modalUtil";

import { CopyExceptionModal } from "../CopyExceptionModal.jsx";
import DocumentUploadPopup from "../DocumentUploadPopup.jsx";
import ExceptionApprovalandSubmitModal from "../ExceptionApprovalandSubmitModal.jsx";
import ExtendApproveByDateModal from "../ExtendApproveByDateModal.jsx";
import ExtendExceptionModal from "../ExtendExceptionModal.jsx";
import MandatoryFieldsModal from "../components/MandatoryRiskAssessmentModal";
import EditApprovalPopupV2 from "./EditApprovalPopupV2/EditApprovalPopupV2";
import { ApproverList } from "./ExceptionDetailsModal";
import { ExceptionData } from "./ExceptionDetailsUtils";

export type ExceptionDetailsContextType = {
  details?: any;
  product?: NameIdPair;
  subProducts?: NameIdPair[];
  id: number;
  loading: boolean;
  approvalHistory: any;
  riskAssessmentFields: ArmorcodeCustomField[];
  copyException: () => object;
  shouldAllowEdit: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  fetchProject: (id?: number) => void;
  exceptionSettings: any;
  onClickAcceptReject: (type: string) => void;
  scopeDetails: any;
  getApproverList: () => { approverlist: ApproverList; step: number };
  allUsers: UserInfoProjectorImpl[];
  deleteException: () => void;
  archiveException: () => void;
  openEditApproval: () => void;
  openApproveByDateExtendPopup: () => void;
  openDocUploadPopup: () => void;
  disabledButtonTooltip: ReactChild | undefined;
  exceptionCommentRef?: React.RefObject<{ refresh: () => void }>;
  documentListRef?: React.RefObject<{ refresh: () => void }>;
  data: any;
  editMode: boolean;
  status: string;
  openExtendPopup: () => void;
  applicableWorkflow: any;
  submitExceptionforApproval: () => void;
  approvalButtonLoading: boolean;
};

const ExceptionDetailsContext =
  React.createContext<ExceptionDetailsContextType>({
    details: undefined,
    id: 0,
    loading: true,
    approvalHistory: [],
    riskAssessmentFields: [],
    copyException: () => ({}),
    shouldAllowEdit: false,
    setEditMode: () => {},
    fetchProject: (id?: number) => {},
    exceptionSettings: {},
    onClickAcceptReject: () => {},
    scopeDetails: {},
    getApproverList: () => ({ approverlist: [], step: 0 }),
    allUsers: [],
    deleteException: () => {},
    archiveException: () => {},
    openEditApproval: () => {},
    openApproveByDateExtendPopup: () => {},
    openDocUploadPopup: () => {},
    disabledButtonTooltip: undefined,
    exceptionCommentRef: undefined,
    documentListRef: undefined,
    data: {},
    editMode: false,
    status: "",
    openExtendPopup: () => {},
    applicableWorkflow: undefined,
    submitExceptionforApproval: () => {},
    approvalButtonLoading: false,
  });

export type ExceptionEntityNavigatorParams = Pick<
  EntityNavigatorParams,
  "payload" | "totalEntities"
>;

export default function ExceptionDetailsProvider({
  children,
  id,
}: {
  id: string | number;
  children: ReactNode | ReactNode[];
}) {
  const modalRenderer = useModalRenderer();
  const [loading, setLoading] = useState(false);
  const [approvalHistory, loadingApprovalHistory] = useAPIResponse<any>(
    () => ExceptionAPI.getApprovalHistory(id),
    [id],
  );

  const [riskAssessmentFields, loadingRiskAssessmentFields] =
    useAPIResponse<any>(
      () => GlobalSettingAPI.getExceptionRiskAssessmentFields(),
      [],
    );

  const exceptionCommentRef = useRef<{ refresh: () => void } | null>(null);
  const documentListRef = useRef<{ refresh: () => void } | null>(null);

  const history = useHistory();
  const [scopeDetails, setScopeDetails] = useState();
  const [editMode, setEditMode] = useState(false);
  const [data, setData] = useState({} as ExceptionData);
  const [approvalHistoryData, setApprovalHistoryData] = useState([]);
  const [exceptionSettings] = useAPIResponse(
    () => GlobalSettingAPI.getExceptionConfig(),
    [],
  );
  const [approvalButtonLoading, setApprovalButtonLoading] = useState(false);

  const { name, endDate, status, customWorkflow, inheritedWorkFlow } = data;

  const shouldAllowEdit = status === "Draft" || status === "Rejected";
  const disabledButtonTooltip = useMemo(() => {
    return shouldAllowEdit ? undefined : (
      <>
        <Vocabulary name="exception" /> can only be modified in Draft status
      </>
    );
  }, [shouldAllowEdit]);

  const applicableWorkflow = customWorkflow || inheritedWorkFlow;

  const fetchProject = useTriggerableEffect(
    (exceptionId?: number) => {
      const effectiveId = exceptionId ?? id;
      if (!effectiveId) return;
      setLoading(true);
      const { promise, cancel } = ExceptionAPI.get(effectiveId);
      promise
        .then((response) => {
          if (response.scope?.teamId) {
            const { promise } = TeamsAPI.getTeam(response.scope?.teamId);
            return promise.then((res) => {
              if (size(res.approvalWorkflow?.approvers) > 0) {
                response.inheritedWorkFlow = res.approvalWorkflow?.approvers;
              }
              return response;
            });
          } else {
            return response;
          }
        })
        .then((response) => {
          setData(response);
          setLoading(false);
        })
        .catch(() => {
          history.replace("/exceptions");
        })
        .finally(() => {
          setLoading(false);
        });
      exceptionCommentRef.current?.refresh();
      return cancel;
    },
    [history, id],
  );

  useEffect(() => {
    if (!data?.scope?.productId) {
      return;
    }
    const calcellables = [];
    const { promise, cancel } = ProductAPI.getAllProductsSpecificFields([], {
      id: [data.scope.productId],
    });
    const { promise: promise2, cancel: cancel2 } = !isEmpty(
      data.scope?.subProductIds,
    )
      ? SubProductAPI.getAllQuick()
      : EmptyCancellableRequest;

    Promise.all([promise, promise2]).then(([products, subProducts]) => {
      const productName =
        find(products, { id: data.scope.productId })?.name ??
        data.scope.productId;
      const subProductIds = !isEmpty(data.scope?.subProductIds)
        ? data.scope.subProductIds.map((subProductId: number) =>
            find(subProducts, { id: subProductId }),
          )
        : undefined;

      const subProductNames = subProductIds?.map((sp: any) => sp?.name) ?? [];

      // @ts-expect-error
      setScopeDetails({ productName, subProductNames });
    });

    calcellables.push(cancel, cancel2);
  }, [data]);

  const fetchApprovalHistory = useCallback(() => {
    const { promise, cancel } = ExceptionAPI.getApprovalHistory(id);
    promise
      .then((response) => {
        setApprovalHistoryData(response);
      })
      .catch((err) => {
        console.error(err);
      });
    return cancel;
  }, [id]);

  useEffect(() => {
    return fetchApprovalHistory();
  }, [fetchApprovalHistory]);

  const [allUsers] = useAPIResponse(() => UserAPI.getAll(), []);
  const [approvers] = useAPIResponse(() => TeamsAPI.getApprovers(), []);
  const activeUser = useActiveUserContext();

  const getApproverList = useCallback(() => {
    const approverlist: {
      displayName: string;
      type: string;
      approvedBy?: any;
      notes: string;
      updatedAt: any;
      updatedTitle: string;
      resend: boolean;
    }[] = [];
    let step = 0;
    const userMap = {};
    // @ts-expect-error
    forEach(allUsers, (u) => (userMap[u.id] = u));
    // @ts-expect-error
    forEach(approvers, (u) => (userMap[u.id] = u));
    if (applicableWorkflow && applicableWorkflow.length > 0) {
      applicableWorkflow
        .sort((a: any, b: any) => a.sequence - b.sequence)
        .forEach((approver: any) => {
          const result = find(approvalHistoryData, (value) => {
            // if roleId is not null, that means Approval is from a Role and not individual user
            // @ts-expect-error
            const isApprovedWithRole = value.roleId !== null;
            const isExternalUser = approver.reviewerType === "EXTERNAL_USER";
            if (isExternalUser) {
              // @ts-expect-error
              return value?.createdBy === approver.externalReviewerEmail;
            }

            return isApprovedWithRole
              ? // @ts-expect-error
                value.roleId === approver.reviewerId
              : // @ts-expect-error
                value.userId === approver.reviewerId;
          });
          let approvedBy;
          // @ts-expect-error

          if (result?.roleId) {
            // @ts-expect-error

            approvedBy = find(allUsers, { id: result.userId })?.displayName;
          }
          // @ts-expect-error

          const user = userMap[approver.reviewerId] || {
            displayName: approver.externalReviewerEmail,
          };
          const displayName = `${user.displayName}${user.type === "ROLE" ? "(Role)" : ""}`;
          const nextReviewer = data?.nextReviewer;

          const isAwaitingApproval =
            approver.reviewerType === "EXTERNAL_USER"
              ? nextReviewer?.email === approver.externalReviewerEmail
              : nextReviewer?.id === approver.reviewerId;
          if (result) {
            //@ts-expect-error
            if (result.action.toLowerCase() === "approved") {
              step = approver.sequence;
              approverlist.push({
                displayName,
                type: "APPROVED",
                approvedBy,
                notes: "Note: " + get(result, "notes", ""),
                //@ts-expect-error
                updatedAt: result.updatedAt,
                updatedTitle: "Approved on ",
                resend: false,
              });
              //@ts-expect-error
            } else if (result.action.toLowerCase() === "rejected") {
              step = approver.sequence;

              approverlist.push({
                displayName,
                type: "REJECTED",
                approvedBy,
                notes: "Notes: " + get(result, "notes", ""),
                //@ts-expect-error
                updatedAt: result.updatedAt,
                updatedTitle: "Rejected on ",
                resend: false,
              });
            }
          } else if (isAwaitingApproval) {
            approverlist.push({
              displayName,
              type: "PENDING",
              notes: "Awaiting approval",
              updatedAt: "",
              updatedTitle: "",
              resend: false,
            });
          } else {
            approverlist.push({
              displayName,
              type: "PENDING",
              notes: "",
              updatedAt: "",
              updatedTitle: "",
              resend: false,
            });
          }
        });
    }
    return { approverlist, step };
  }, [
    allUsers,
    applicableWorkflow,
    approvalHistoryData,
    approvers,
    data?.nextReviewer,
  ]);

  const deleteException = useCallback(async () => {
    ModalUtil.show({
      content: (
        <DeletePopup
          // @ts-ignore
          confirmText={"Delete"}
          onDelete={() => deleteExceptionById()}
        />
      ),
      afterClose: () => {
        fetchProject();
      },
    });
  }, [id, fetchProject]);

  const deleteExceptionById = useCallback(async () => {
    await ExceptionAPI.deleteException(id).promise;
    message.success("Delete request accepted. Please check after sometime");
    // This is needed as Delete pop need some time to unmount
    setTimeout(() => {
      history.goBack();
    }, 700);
  }, [history, id]);

  const copyException = useCallback(async () => {
    console.log("copy exception called");
    ModalUtil.show({
      // @ts-expect-error
      content: <CopyExceptionModal currentName={name} id={id} />,
    });
  }, [name, id]);

  const acceptRejectException = useCallback(
    async (notes, type) => {
      await ExceptionAPI[
        type === "Accept" ? "approveException" : "denyException"
      ](id, { notes }).promise;
      message.success(`Successfully ${type}ed ${getVocabulary("exception")}`);
      fetchProject();
      fetchApprovalHistory();
    },
    [id, fetchProject, fetchApprovalHistory],
  );

  const onClickAcceptReject = useCallback(
    (type: string) => {
      const userMap = {};
      //@ts-expect-error
      forEach(allUsers, (u) => (userMap[u.id] = u));
      //@ts-expect-error
      forEach(approvers, (u) => (userMap[u.id] = u));
      const pendingApproverNames = filter(
        applicableWorkflow,
        ({ reviewerId, sequence }) =>
          reviewerId !== activeUser.id &&
          sequence >=
            find(applicableWorkflow, { reviewerId: activeUser.id })?.sequence,
      );
      const allApproverNamesArray = map(
        pendingApproverNames,
        //@ts-expect-error
        (i) => i.externalReviewerEmail || userMap[i.reviewerId].displayName,
      );
      const joinedApproverNames =
        allApproverNamesArray.length < 2
          ? allApproverNamesArray.join()
          : `${allApproverNamesArray
              .slice(0, -1)
              .join(", ")} and ${allApproverNamesArray.slice(-1)}`;
      const date = moment(endDate).startOf("days");
      const currentDate = moment().startOf("days");
      if (type === "Accept" && date.isBefore(currentDate)) {
        ModalUtil.show({
          content: (
            <ExceptionApprovalandSubmitModal
              //@ts-expect-error
              showMessage={
                <div>
                  <div>
                    {" "}
                    Are you sure you want to approve this{" "}
                    <Vocabulary name={"exception"} />?
                  </div>
                  <div>
                    {isEmpty(allApproverNamesArray) ? (
                      ""
                    ) : (
                      <>
                        Once approved, this <Vocabulary name={"exception"} />{" "}
                        will go to {joinedApproverNames}.
                      </>
                    )}
                  </div>
                </div>
              }
              okText={
                <>
                  Approve <Vocabulary name={"exception"} />
                </>
              }
              id={id}
              title={`Confirm ${getVocabulary("exception")} approval`}
            />
          ),
          afterClose: () => {
            fetchProject();
            fetchApprovalHistory();
          },
        });
      } else {
        ModalUtil.show({
          content: (
            <ConfirmPopup
              //@ts-expect-error
              title={
                type === "Reject" ? (
                  <>
                    {" "}
                    Deny <Vocabulary name={"exception"} />
                  </>
                ) : (
                  <>
                    {" "}
                    Confirm <Vocabulary name={"exception"} /> approval
                  </>
                )
              }
              promptMessage={
                type === "Reject" ? (
                  <div>
                    <div>
                      You are about to deny this{" "}
                      <Vocabulary name={"exception"} />.
                    </div>
                    <div>
                      Once denied, this <Vocabulary name={"exception"} /> will
                      be sent back to the submitter to address your concerns.
                    </div>
                  </div>
                ) : (
                  <div>
                    <div>
                      {" "}
                      Are you sure you want to approve this{" "}
                      <Vocabulary name={"exception"} />?
                    </div>
                    <div>
                      {isEmpty(allApproverNamesArray) ? (
                        ""
                      ) : (
                        <>
                          Once approved, this <Vocabulary name={"exception"} />{" "}
                          will go to {joinedApproverNames}.
                        </>
                      )}
                    </div>
                  </div>
                )
              }
              note
              type={type === "Reject" ? "delete" : null}
              okText={
                type === "Reject" ? (
                  <>
                    Deny <Vocabulary name={"exception"} />
                  </>
                ) : (
                  <>
                    Approve <Vocabulary name={"exception"} />
                  </>
                )
              }
              //@ts-expect-error
              onOk={(notes) => acceptRejectException(notes, type)}
              modalProps={{
                cancelBtnText: "Cancel",
              }}
            />
          ),
        });
      }
    },
    [
      acceptRejectException,
      activeUser.id,
      allUsers,
      applicableWorkflow,
      approvers,
      endDate,
      fetchApprovalHistory,
      fetchProject,
      id,
    ],
  );

  const openEditApproval = useCallback(() => {
    modalRenderer.show({
      // @ts-expect-error
      content: <EditApprovalPopupV2 editId={id} />,
      onClose: () => {
        fetchProject();
      },
      width: 696,
    });
  }, [fetchProject, id, modalRenderer]);

  const openExtendPopup = useCallback(() => {
    const approverlist = getApproverList();
    ModalUtil.show({
      content: (
        <ExtendExceptionModal
          //@ts-expect-error
          editId={id}
          approverList={approverlist}
          endDate={endDate}
        />
      ),
      afterClose: () => {
        fetchProject();
      },
    });
  }, [fetchProject, id, getApproverList, endDate]);

  const openApproveByDateExtendPopup = useCallback(() => {
    ModalUtil.show({
      //@ts-expect-error
      content: <ExtendApproveByDateModal editId={id} />,
      afterClose: () => {
        fetchProject();
      },
    });
  }, [fetchProject, id]);

  const openDocUploadPopup = useCallback(() => {
    ModalUtil.show({
      //@ts-expect-error
      content: <DocumentUploadPopup id={id} />,
      afterClose: () => documentListRef.current?.refresh(),
    });
  }, [id, documentListRef]);
  const archiveException = useCallback(() => {
    ModalUtil.show({
      content: (
        <ConfirmPopup
          //@ts-expect-error
          promptMessage={
            <>
              You want to archive this <Vocabulary name={"exception"} />
            </>
          }
          okText="Archive"
          //@ts-expect-error
          onOk={async (note) => {
            return ExceptionAPI.triggerArchive(id).promise.then(() => {
              message.success(
                `${getVocabulary("exception")} successfully archived.`,
              );
              history.goBack();
            });
          }}
        />
      ),
    });
  }, [history, id]);

  const showDefaultModal = useCallback(() => {
    const date = moment(endDate).startOf("days");
    const currentDate = moment().startOf("days");
    if (date.isBefore(currentDate)) {
      ModalUtil.show({
        content: (
          <ExceptionApprovalandSubmitModal
            // @ts-expect-error

            showMessage={
              <>
                You're about to submit this <Vocabulary name={"exception"} />{" "}
                for approval. Once submitted you will not be able to edit this{" "}
                <Vocabulary name={"exception"} /> .
              </>
            }
            okText="Submit"
            id={id}
            history={history}
            title={"Are you sure"}
            isSubmittingForApproval={true}
          />
        ),
        afterClose: () => setApprovalButtonLoading(false),
      });
    } else {
      ModalUtil.show({
        content: (
          <ConfirmPopup
            // @ts-expect-error

            type=""
            promptMessage={
              <>
                You're about to submit this <Vocabulary name={"exception"} />{" "}
                for approval. Once submitted you will not be able to edit this{" "}
                <Vocabulary name={"exception"} />.
              </>
            }
            okText="Submit"
            onOk={async () => {
              return ExceptionAPI.triggerSubmit({}, id, false).promise.then(
                () => {
                  message.success("Submitted successfully.");
                  history.goBack();
                },
              );
            }}
          />
        ),
        afterClose: () => setApprovalButtonLoading(false),
      });
    }
  }, [history, id, endDate]);

  const validateRiskAssessmentCustomFields = useCallback(async () => {
    setApprovalButtonLoading(true);
    const { promise, cancel } =
      GlobalSettingAPI.getExceptionRiskAssessmentFields();
    promise.then(({ riskAssessmentFields }) => {
      //required risk assessment fields from global settings
      const requiredGlobalSettingriskFields = map(
        filter(riskAssessmentFields, (field) => field.required),
        (I) => I.id,
      );

      //if exception risk assessment fields is empty
      //all required fields are missing
      //ELSE
      //intersection between exception risk fields and global settings fields are missing

      const mandatoryRiskAssessmentFields = isEmpty(data.riskAssessmentFields)
        ? requiredGlobalSettingriskFields
        : difference(
            requiredGlobalSettingriskFields,
            data.riskAssessmentFields.map((i) => i.id),
          );

      const hasMissingRequiredFields = !isEmpty(mandatoryRiskAssessmentFields);
      if (hasMissingRequiredFields) {
        ModalUtil.show({
          content: (
            <MandatoryFieldsModal
              riskAssessmentFields={filter(riskAssessmentFields, (field) =>
                mandatoryRiskAssessmentFields.includes(field.id),
              )}
              fetchProject={fetchProject}
              exception={data}
              // @ts-expect-error
              history={history}
            />
          ),
          afterClose: () => setApprovalButtonLoading(false),
        });
      } else {
        showDefaultModal();
      }
    });
    return cancel;
  }, [data, fetchProject, history, showDefaultModal]);

  const submitExceptionforApproval = useCallback(async () => {
    await validateRiskAssessmentCustomFields();
  }, [validateRiskAssessmentCustomFields]);

  const value = useMemo(
    () => ({
      details: data!,
      id: +id,
      loading: loading || loadingApprovalHistory || loadingRiskAssessmentFields,
      approvalHistory: approvalHistory || [],
      riskAssessmentFields: _.sortBy(
        riskAssessmentFields?.riskAssessmentFields,
        (field) => {
          return field.type === "Text-Long" ? 1 : 0;
        },
      ),
      shouldAllowEdit: shouldAllowEdit,
      copyException: copyException,
      setEditMode: setEditMode,
      fetchProject,
      exceptionSettings,
      onClickAcceptReject,
      scopeDetails,
      getApproverList,
      allUsers,
      deleteException,
      archiveException,
      openEditApproval,
      openApproveByDateExtendPopup,
      openDocUploadPopup,
      disabledButtonTooltip,
      exceptionCommentRef,
      documentListRef,
      data,
      editMode,
      status,
      openExtendPopup,
      applicableWorkflow,
      submitExceptionforApproval,
      approvalButtonLoading,
    }),
    [
      approvalHistory,
      id,
      loadingApprovalHistory,
      loadingRiskAssessmentFields,
      riskAssessmentFields,
      shouldAllowEdit,
      copyException,
      setEditMode,
      fetchProject,
      exceptionSettings,
      onClickAcceptReject,
      scopeDetails,
      getApproverList,
      allUsers,
      deleteException,
      archiveException,
      openEditApproval,
      openApproveByDateExtendPopup,
      openDocUploadPopup,
      disabledButtonTooltip,
      exceptionCommentRef,
      documentListRef,
      data,
      editMode,
      status,
      openExtendPopup,
      applicableWorkflow,
      submitExceptionforApproval,
      approvalButtonLoading,
      loading,
    ],
  );

  return (
    <ExceptionDetailsContext.Provider value={value}>
      {children}
      {modalRenderer.context}
    </ExceptionDetailsContext.Provider>
  );
}

export const useExceptionDetails = () => {
  return useContext(ExceptionDetailsContext);
};
